import React, { useState, useRef, useCallback, useEffect } from 'react';
import { makeStyles, Tooltip, useMediaQuery, ClickAwayListener } from '@material-ui/core';
import { statusColor, formattedDateRange, statusTitle } from '../Home/Forecast/utils';

const useStyles = makeStyles(() => ({
    tooltip: {
        '& .MuiTooltip-tooltip': {
            backgroundColor: 'white',
            color: 'black',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            fontSize: '12px',
            padding: '12px',
            maxWidth: '250px',
        },
    },
    tooltipStatus: {
        fontWeight: 600,
        marginBottom: '4px',
    },
    tooltipDate: {
        fontSize: '11px',
        opacity: 0.7,
        marginBottom: '8px',
    },
    tooltipComment: {
        marginBottom: '8px',
    },
    tooltipSource: {
        fontSize: '11px',
        opacity: 0.7,
        marginBottom: '8px',
    },
    tooltipUser: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    tooltipUserImage: {
        width: '24px',
        height: '24px',
        borderRadius: '50%',
        backgroundColor: '#f0f0f0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '10px',
        fontWeight: 600,
    },
    tooltipUserInfo: {
        flex: 1,
    },
    tooltipUserName: {
        fontWeight: 600,
        fontSize: '11px',
    },
    tooltipUserTitle: {
        fontSize: '10px',
        opacity: 0.7,
    },
}));

interface ForecastTooltipProps {
    forecast: {
        status?: string;
        comment?: string | null;
        startDate?: string;
        endDate?: string;
        externalSource?: string | null;
        title?: string; // For vacation planner compatibility
    };
    user?: {
        name?: string | null;
        title?: string | null;
        profilePicture?: string | null;
    };
    children: React.ReactElement;
    showTooltip?: boolean;
    statusColor?: string; // Allow override for vacation planner
}

const ForecastTooltip: React.FC<ForecastTooltipProps> = ({
    forecast,
    user,
    children,
    showTooltip = true,
    statusColor: overrideStatusColor
}) => {
    const classes = useStyles();
    const isMobile = useMediaQuery('(max-width:600px)');
    const [mobileOpen, setMobileOpen] = useState(false);
    const autoDismissTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

    const closeMobileTooltip = useCallback(() => {
        setMobileOpen(false);
        if (autoDismissTimer.current) {
            clearTimeout(autoDismissTimer.current);
            autoDismissTimer.current = null;
        }
    }, []);

    const handleTap = useCallback(() => {
        if (!isMobile) return;
        setMobileOpen(true);
        if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
        autoDismissTimer.current = setTimeout(() => {
            setMobileOpen(false);
        }, 2000);
    }, [isMobile]);

    // Clean up timer on unmount
    useEffect(() => {
        return () => {
            if (autoDismissTimer.current) clearTimeout(autoDismissTimer.current);
        };
    }, []);

    if (!showTooltip) {
        return children;
    }

    const textColor = overrideStatusColor || statusColor(forecast.status);
    const displayTitle = forecast.title || statusTitle(forecast.status);

    const tooltipContent = (
        <>
            <div className={classes.tooltipStatus} style={{ color: textColor }}>
                {displayTitle}
            </div>
            <div className={classes.tooltipDate}>
                {formattedDateRange(forecast)}
            </div>
            {forecast.comment && (
                <div className={classes.tooltipComment}>
                    {forecast.comment}
                </div>
            )}
            {forecast.externalSource && (
                <div className={classes.tooltipSource}>
                    From {forecast.externalSource}
                </div>
            )}
            {user && (
                <div className={classes.tooltipUser}>
                    <div className={classes.tooltipUserImage}>
                        {user.profilePicture ? (
                            <img
                                src={'https://d378wr66tg5qg7.cloudfront.net/' + user.profilePicture}
                                alt={user.name || ''}
                                style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                            />
                        ) : (
                            user.name?.charAt(0)?.toUpperCase()
                        )}
                    </div>
                    <div className={classes.tooltipUserInfo}>
                        <div className={classes.tooltipUserName}>
                            {user.name}
                        </div>
                        {user.title && (
                            <div className={classes.tooltipUserTitle}>
                                {user.title}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );

    if (isMobile) {
        return (
            <ClickAwayListener onClickAway={closeMobileTooltip}>
                <div style={{ width: '100%', height: '100%' }}>
                    <Tooltip
                        classes={{ popper: classes.tooltip }}
                        open={mobileOpen}
                        disableFocusListener
                        disableHoverListener
                        disableTouchListener
                        title={tooltipContent}
                    >
                        {React.cloneElement(children, {
                            onClick: handleTap,
                        })}
                    </Tooltip>
                </div>
            </ClickAwayListener>
        );
    }

    return (
        <Tooltip
            classes={{ popper: classes.tooltip }}
            title={tooltipContent}
        >
            {children}
        </Tooltip>
    );
};

export default ForecastTooltip; 